import React, { useState } from 'react';
import Icon, { QuestionCircleOutlined } from '@ant-design/icons';
import AnalysisSvg from '@icons/Eco/analysis.svg';
import { Button, Modal, Spin, Tag } from 'antd';
import { humanize } from '@src/utils';
import ErrorBoundary from '../../common/Retry/ErrorBoundary';
import {
  HeightLight,
  Wrapper,
  IconWrapper,
  SummaryWrapper,
  Title,
  Content,
  Rooter,
  Affect,
  RootCause,
} from './styled';

export const enum ROOT_CAUSE {
  EXPERIENCE_ONLY = 'Client',
  BACKEND_ONLY = 'Backend',
  COMBINATION = 'Client+Backend',
}

const GetMetrics = (context) => {
  return context.nodeMetricList;
};

const GenDimensionDisplay = (dimensions) => {
  return Object.entries(dimensions)
    .map(([key, value]) => {
      if (key === 'all_traffic') return 'All Traffic';
      return `${key} ${value}`;
    })
    .join(' and ');
};
const GenMetricDisplay = (metrics, preposition = 'is') => {
  return metrics
    .filter((metric) => metric.type === 'impacted')
    .map((metric) => {
      return (
        <>
          <HeightLight>{metric.metricName}</HeightLight> {preposition}{' '}
          <HeightLight>{`${humanize(metric.value, metric.unit)} ${metric.unit === 'ms' ? '' : metric.unit}`}</HeightLight>
        </>
      );
    })
    .reduce((prev, curr) => [prev, ' and ', curr]);
};

const IssueSummary = ({ metrics, alertSummary, loading }) => {
  const [open, setOpen] = useState(false);
  const showModal = () => {
    setOpen(true);
  };
  const hideModal = () => {
    setOpen(false);
  };

  const GenSummary = () => {
    const experience = GetMetrics(metrics['session']);

    const backendMetric = GetMetrics(metrics['span']).filter(
      (item) => item.filterDisplayContent === alertSummary.rootCauseDisplayContent,
    );

    switch (alertSummary.rootCauseCategory) {
      case ROOT_CAUSE.COMBINATION:
        return (
          <>
            <Affect>
              {GenMetricDisplay(experience[0].metrics)} in the past{' '}
              <HeightLight>{alertSummary.duration}</HeightLight> for{' '}
              <HeightLight>{humanize(alertSummary.impactedUsers)}</HeightLight> unique devices for{' '}
              <HeightLight>{GenDimensionDisplay(experience[0].displayFilters)}</HeightLight>.
            </Affect>
            <RootCause>
              It is possibly caused by{' '}
              {backendMetric
                .map((item) => {
                  return (
                    <>
                      <HeightLight>{GenDimensionDisplay(item.displayFilters)}</HeightLight> having a{' '}
                      {GenMetricDisplay(item.metrics, 'of')}
                    </>
                  );
                })
                .reduce((prev, curr) => [prev, ', ', curr])}
              .
            </RootCause>
          </>
        );
      case ROOT_CAUSE.EXPERIENCE_ONLY:
        return (
          <Affect>
            {GenMetricDisplay(experience[0].metrics)} in the past{' '}
            <HeightLight>{alertSummary.duration}</HeightLight> for{' '}
            <HeightLight>{humanize(alertSummary.impactedUsers)}</HeightLight> unique devices for{' '}
            <HeightLight>{GenDimensionDisplay(experience[0].displayFilters)}</HeightLight>.
          </Affect>
        );
      case ROOT_CAUSE.BACKEND_ONLY:
        return (
          <Affect>
            {backendMetric
              .map((item) => {
                return (
                  <>
                    <HeightLight>{GenDimensionDisplay(item.displayFilters)}</HeightLight> is having
                    a {GenMetricDisplay(item.metrics, 'of')}
                  </>
                );
              })
              .reduce((prev, curr) => [prev, ', ', curr])}
          </Affect>
        );
    }
    return null;
  };
  return (
    <Wrapper>
      <IconWrapper>
        <Icon component={AnalysisSvg} style={{ fontSize: '40px', paddingRight: '2px' }} />
      </IconWrapper>
      <SummaryWrapper>
        <Title>{'Issue Summary'}</Title>
        <ErrorBoundary>
          <Content>
            {loading ? <Spin style={{ display: 'block', height: '50px' }} /> : <GenSummary />}
          </Content>
        </ErrorBoundary>
        <Rooter>
          <Button
            onClick={showModal}
            style={{ color: '#1F1F1F', borderColor: '#d9d9d9' }}
            icon={<QuestionCircleOutlined style={{ color: '#777' }} />}
          >
            How was this summary made?
          </Button>
          <Modal
            title="How was this summary made?"
            open={open}
            onOk={hideModal}
            onCancel={hideModal}
            footer={[
              <Button key="ok" ghost type="primary" onClick={hideModal}>
                Ok
              </Button>,
            ]}
          >
            <p>
              The summary for an Experience Alert is based on anomalies the artificial intelligence
              detects based on historical performance and a comparison with non-impacted cohort
              dimensions and metrics.
            </p>
          </Modal>
        </Rooter>
      </SummaryWrapper>
    </Wrapper>
  );
};

export default IssueSummary;
